import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Portal from '../Portal';
import { useTracking } from '../../hooks/useTracking';

interface GenerationResultModalProps {
  open: boolean;
  videoUrl: string;
  onClose: () => void;
  sourcePage?: 'lander-1' | 'image-to-video' | 'homepage' | 'i2v';
}

export default function GenerationResultModal({
  open,
  videoUrl,
  onClose,
  sourcePage = 'lander-1',
}: GenerationResultModalProps) {
  const [isNavigating, setIsNavigating] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const navigationIntentRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();
  const { track } = useTracking();

  const isI2vFlow = sourcePage === 'i2v';

  // Lock scroll when overlay is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';

      const preventScroll = (e: TouchEvent) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
        }
      };

      document.body.addEventListener('touchmove', preventScroll, { passive: false });

      return () => {
        document.body.style.overflow = '';
        document.body.removeEventListener('touchmove', preventScroll);
      };
    }
  }, [open]);

  // Reset state when modal opens with new video
  React.useEffect(() => {
    if (open) {
      navigationIntentRef.current = false;
      setIsNavigating(false);
    }
  }, [open, videoUrl]);

  if (!open) return null;

  const handleGenerateAnother = async () => {
    const eventName = sourcePage === 'i2v' ? 'i2v_result_cta_clicked' : 'lander1_remove_watermark_clicked';
    track(eventName, {
      source_page: sourcePage,
      cta_type: sourcePage === 'i2v' ? 'get_more_videos' : sourcePage === 'lander-1' ? 'remove_watermark' : 'generate_another',
      video_url: videoUrl,
      has_url_hash: !!window.location.hash,
      timestamp: Date.now(),
    });

    if (navigationIntentRef.current) {
      console.log('[GenerationResultModal] Navigation already in progress, ignoring duplicate click');
      return;
    }

    if (isNavigating) {
      console.log('[GenerationResultModal] Navigation already in progress (state check), ignoring duplicate click');
      return;
    }

    navigationIntentRef.current = true;
    setIsNavigating(true);

    await new Promise(resolve => setTimeout(resolve, 100));

    if (sourcePage === 'image-to-video' || sourcePage === 'homepage') {
      window.location.reload();
    } else {
      if (window.location.hash) {
        console.log('[GenerationResultModal] Cleaning URL hash before navigation to prevent OAuth issues');
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }

      localStorage.removeItem('genanything_lander1_modal_state');
      console.log('[GenerationResultModal] Cleared lander-1 modal state before navigating to /upgrade');

      console.log('[GenerationResultModal] Navigating to /upgrade (breaking history chain)');
      window.location.href = '/upgrade';
    }
  };

  // CTA button content (varies by flow)
  const ctaButtonContent = (() => {
    if (sourcePage === 'i2v') {
      return (
        <>
          <svg
            className="w-4 h-4 md:w-5 md:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.64 0 8.577 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.64 0-8.577-3.007-9.963-7.178Z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
          Remove Watermark
        </>
      );
    }
    if (sourcePage === 'lander-1') {
      return (
        <>
          <svg
            className="w-4 h-4 md:w-5 md:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.64 0 8.577 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.64 0-8.577-3.007-9.963-7.178Z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
          Remove Watermark
        </>
      );
    }
    return (
      <>
        <svg
          className="w-4 h-4 md:w-5 md:h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4.5v15m7.5-7.5h-15"
          />
        </svg>
        Generate Another Video
      </>
    );
  })();

  // Shared button event handlers
  const ctaClickHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setUserHasInteracted(true);
    handleGenerateAnother();
  };

  const ctaTouchStartHandler = (e: React.TouchEvent) => {
    e.stopPropagation();
    setUserHasInteracted(true);
  };

  const ctaTouchEndHandler = (e: React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleGenerateAnother();
  };

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-3 md:p-4"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
        }}
      >
        <div className={`w-full max-w-[90vw] flex flex-col ${isI2vFlow ? 'max-h-[90vh]' : 'h-[90vh] max-h-[90vh]'}`}>
          {/* Main Content Card */}
          <div className={`bg-bg-panel border border-border rounded-modal shadow-lg flex flex-col overflow-hidden ${isI2vFlow ? '' : 'h-full'}`}>
            {/* Header */}
            <div className="flex justify-center items-center p-3 md:p-4 lg:p-6 flex-shrink-0 border-b border-border">
              <h2 className="text-h1 md:text-h1 lg:text-display font-medium text-white">
                Your Video is Complete!
              </h2>
            </div>

            {/* Video Player - Takes up available space */}
            <div className={`${isI2vFlow ? 'min-h-0' : 'flex-1 min-h-0'} flex items-center justify-center p-3 md:p-4 lg:p-6`}>
              <div className="relative w-full h-full bg-black/20 rounded-lg overflow-hidden flex items-center justify-center">
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  playsInline
                  className="w-full h-full max-h-[60vh] object-contain rounded-lg"
                  onError={(e) => {
                    console.error('[GenerationResultModal] Video load error:', e);
                  }}
                >
                  Your browser does not support the video tag.
                </video>

              </div>
            </div>

            {/* Action Button - Fixed at bottom */}
            <div className="p-3 md:p-4 lg:p-6 flex-shrink-0 border-t border-border">
              {sourcePage === 'lander-1' || sourcePage === 'i2v' ? (
                <button
                  onClick={ctaClickHandler}
                  onTouchStart={ctaTouchStartHandler}
                  onTouchEnd={ctaTouchEndHandler}
                  disabled={isNavigating}
                  className="w-full text-white btn-glow px-4 md:px-6 py-3 min-h-[44px] rounded-button font-semibold transition-colors flex items-center justify-center gap-2 text-body disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6C3BAA 50%, #5A2E91 100%)' }}
                >
                  {ctaButtonContent}
                </button>
              ) : (
                <button
                  onClick={ctaClickHandler}
                  onTouchStart={ctaTouchStartHandler}
                  onTouchEnd={ctaTouchEndHandler}
                  disabled={isNavigating}
                  className="w-full bg-accent text-white btn-glow px-4 md:px-6 py-3 min-h-[44px] rounded-button font-medium hover:bg-accent-hover transition-colors flex items-center justify-center gap-2 text-body disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {ctaButtonContent}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
